/*
 * Hollis CRIB configurator core — pure logic, no DOM.
 * Config format: docs/superpowers/plans/2026-10-27-hollis-crib.md ("Config format").
 * Browser: window.CribCore (used by assets/crib-configurator.js). Node: module.exports (tests/crib-core.test.js).
 * Prices are integers in cents. The rig variant price is the base build; upgrades add their variant price.
 */
(function (root) {
  'use strict';

  var COLORS = {
    black: { label: 'Black', hex: '#1b1d21' },
    red: { label: 'Red', hex: '#c8202f' },
    blue: { label: 'Blue', hex: '#1f4fbf' },
    aqua: { label: 'Aqua', hex: '#17aebb' },
    lime: { label: 'Lime', hex: '#6cc72f' },
    pink: { label: 'Pink', hex: '#dd2f95' },
    camo: { label: 'Camo', hex: '#8a8a63' }
  };

  function copy(o) { var r = {}; for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) r[k] = o[k]; return r; }
  function find(list, id) { for (var i = 0; i < list.length; i++) if (list[i].id === id) return list[i]; return null; }
  function stepsOfType(config, type) { return config.steps.filter(function (s) { return s.type === type; }); }
  function colorLabel(id) { return COLORS[id] ? COLORS[id].label : id; }

  // Every colour group, including add-on colour groups (tagged with the add-on that owns them).
  function colorGroups(config) {
    var groups = [];
    config.steps.forEach(function (s) {
      if (s.type === 'colors') s.groups.forEach(function (g) { groups.push({ group: g, owner: null }); });
      if (s.type === 'addons') s.options.forEach(function (o) { if (o.color_group) groups.push({ group: o.color_group, owner: o.id }); });
    });
    return groups;
  }

  function normalize(config, input) {
    var d = config.defaults, sel = {};
    stepsOfType(config, 'choice').forEach(function (s) {
      sel[s.id] = find(s.options, input[s.id]) ? input[s.id] : d[s.id];
      var opt = find(s.options, sel[s.id]);
      if (opt && opt.sizes) {
        sel.size = opt.sizes.indexOf(input.size) >= 0 ? input.size : (opt.sizes.indexOf(d.size) >= 0 ? d.size : opt.sizes[0]);
      }
    });
    stepsOfType(config, 'addons').forEach(function (s) {
      s.options.forEach(function (o) { if (input['addon:' + o.id] === true) sel['addon:' + o.id] = true; });
    });
    colorGroups(config).forEach(function (cg) {
      if (cg.owner && !sel['addon:' + cg.owner]) return;
      var g = cg.group, v = input[g.id];
      sel[g.id] = g.colors.indexOf(v) >= 0 ? v : (g.colors.indexOf(d[g.id]) >= 0 ? d[g.id] : g.colors[0]);
    });
    return sel;
  }

  function defaultSelection(config) { return normalize(config, copy(config.defaults)); }

  function matches(when, sel) {
    for (var k in when) {
      var want = when[k], have = sel[k];
      if (Object.prototype.toString.call(want) === '[object Array]') { if (want.indexOf(have) < 0) return false; }
      else if (have !== want) return false;
    }
    return true;
  }

  // { total, lines: [{ sku, label, id, price }], missing: [sku] }
  function evaluate(config, sel, prices, basePrice) {
    var total = basePrice, lines = [], missing = [];
    config.upgrades.forEach(function (u) {
      if (!matches(u.when, sel)) return;
      var p = prices[u.sku];
      if (!p || !p.available || typeof p.price !== 'number' || !isFinite(p.price)) { missing.push(u.sku); return; }
      total += p.price;
      lines.push({ sku: u.sku, label: u.label, id: p.id, price: p.price });
    });
    return { total: total, lines: lines, missing: missing };
  }

  // For each candidate value of `key`: { total, ok, delta } where delta is vs the cheapest ok candidate.
  function optionDeltas(config, sel, prices, basePrice, key, values) {
    var res = {}, min = Infinity;
    values.forEach(function (v) {
      var s = copy(sel);
      if (v === false) delete s[key]; else s[key] = v;
      var e = evaluate(config, normalize(config, s), prices, basePrice);
      res[v] = { total: e.total, ok: e.missing.length === 0 };
      if (res[v].ok && e.total < min) min = e.total;
    });
    values.forEach(function (v) { res[v].delta = res[v].ok ? res[v].total - min : null; });
    return res;
  }

  function summary(config, sel) {
    var out = [];
    config.steps.forEach(function (s) {
      if (s.type === 'choice') {
        var opt = find(s.options, sel[s.id]);
        out.push([s.label, opt.label + (opt.sizes && sel.size ? ' · ' + sel.size : '')]);
      } else if (s.type === 'colors') {
        s.groups.forEach(function (g) { out.push([g.label + ' color', colorLabel(sel[g.id])]); });
      } else if (s.type === 'addons') {
        var on = s.options.filter(function (o) { return sel['addon:' + o.id]; }).map(function (o) {
          return o.label + (o.color_group ? ' (' + colorLabel(sel[o.color_group.id]) + ')' : '');
        });
        out.push([s.label, on.length ? on.join(', ') : 'None']);
      }
    });
    return out;
  }

  function cartItems(config, sel, prices, rigVariantId, buildId) {
    var ev = evaluate(config, sel, prices, 0);
    if (ev.missing.length) return null;
    // Two matched upgrades can resolve to the same variant (e.g. overlapping `when` rules) — the
    // cart must only ever carry one part line per variant id, or _crib_parts and the actual part
    // lines in the cart go out of sync and the build is wrongly treated as incomplete/orphaned.
    var seenIds = {}, dedupedLines = [];
    ev.lines.forEach(function (l) {
      if (Object.prototype.hasOwnProperty.call(seenIds, l.id)) return;
      seenIds[l.id] = true;
      dedupedLines.push(l);
    });
    var partIds = dedupedLines.map(function (l) { return l.id; }).sort(function (a, b) { return a - b; });
    var props = { _crib_build: buildId, _crib_role: 'rig', _crib_parts: partIds.join(',') };
    var lines = summary(config, sel);
    lines.forEach(function (l) { props[l[0]] = l[1]; });
    props._crib_sheet = JSON.stringify({ v: 1, system: config.system, title: config.title, lines: lines });
    var items = [{ id: rigVariantId, quantity: 1, properties: props }];
    dedupedLines.forEach(function (l) {
      items.push({ id: l.id, quantity: 1, properties: { _crib_build: buildId, _crib_role: 'part' } });
    });
    return items;
  }

  function encodeBuild(sel) { return JSON.stringify(sel); }
  function decodeBuild(config, str) {
    if (!str) return null;
    try { var o = JSON.parse(str); return (o && typeof o === 'object') ? normalize(config, o) : null; }
    catch (e) { return null; }
  }

  function renderLayers(config, sel, angle) {
    var r = config.render;
    if (!r || !r.available) return null;
    var id = r.key.map(function (k) { return sel[k]; }).join('-') + '-' + angle;
    if (r.available.indexOf(id) < 0) return null;
    var urls = [];
    r.layers.forEach(function (l) {
      // colour = the current selection for l.color if set, else l.default, else skip this layer
      // entirely (never emit a "-undefined.webp" URL).
      var c = sel[l.color];
      if (c === undefined) c = l.default;
      if (c === undefined) return;
      urls.push(r.base_url + 'crib-' + id + '-' + l.name + '-' + c + '.webp');
    });
    return urls;
  }

  // Alt text for the main preview's base layer — describes exactly what the layers show: the choices
  // in render.key (wing / plate / harness) plus the non-add-on colour groups, e.g.
  // "Hollis CRIB Single Tank rig — ST22 wing in red, Aluminum backplate, Solo harness, black backpad,
  // lime shoulder pads". Bands, sizes and add-ons aren't drawn as choices, so they're left out.
  function previewAlt(config, sel) {
    var r = config.render || {}, parts = [], colorStep = stepsOfType(config, 'colors')[0];
    var colorsById = {};
    if (colorStep) colorStep.groups.forEach(function (g) { colorsById[g.id] = g; });
    (r.key || []).forEach(function (k) {
      var step = find(config.steps, k);
      var opt = step && step.options ? find(step.options, sel[k]) : null;
      if (!opt) return;
      var noun = step.label.toLowerCase();
      var phrase = (noun === 'wing' || noun === 'harness' || noun === 'backplate') ?
        opt.label + ' ' + noun : step.label + ' ' + opt.label.toLowerCase();
      if (k === 'wing' && colorsById.wing_color && sel.wing_color) phrase += ' in ' + colorLabel(sel.wing_color).toLowerCase();
      parts.push(phrase);
    });
    if (colorStep) colorStep.groups.forEach(function (g) {
      if (g.id === 'wing_color' || !sel[g.id]) return;
      parts.push(colorLabel(sel[g.id]).toLowerCase() + ' ' + ideaRoleLabel(g.id));
    });
    return 'Hollis CRIB ' + config.title + ' rig' + (parts.length ? ' — ' + parts.join(', ') : '');
  }

  function newBuildId() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 7); }

  // "Color ideas" thumbnail strip — curated mixes, defined once here so they're reusable/testable
  // without any DOM. A mix is only offered for a config where every colour it names is valid in
  // the corresponding group (so e.g. a sidemount config with no backpad/shoulder groups, or a
  // group missing one of these colours, silently drops it instead of erroring).
  var IDEA_MIXES = [
    { id: 'mix-red-black-black', wing_color: 'red', backpad: 'black', shoulder: 'black' },
    { id: 'mix-black-red-red', wing_color: 'black', backpad: 'red', shoulder: 'red' },
    { id: 'mix-camo-black-lime', wing_color: 'camo', backpad: 'black', shoulder: 'lime' },
    { id: 'mix-aqua-black-aqua', wing_color: 'aqua', backpad: 'black', shoulder: 'aqua' },
    { id: 'mix-black-pink-pink', wing_color: 'black', backpad: 'pink', shoulder: 'pink' },
    { id: 'mix-blue-black-lime', wing_color: 'blue', backpad: 'black', shoulder: 'lime' }
  ];

  function ideaRoleLabel(groupId) {
    if (groupId === 'wing_color') return 'wing';
    if (groupId === 'backpad') return 'backpad';
    if (groupId === 'shoulder') return 'shoulder pads';
    if (groupId === 'lx2_color') return 'LX2 pockets';
    return groupId;
  }

  function ideaLabel(colors, order) {
    var parts = [];
    order.forEach(function (gid) {
      if (colors[gid] === undefined) return;
      parts.push(colorLabel(colors[gid]) + ' ' + ideaRoleLabel(gid));
    });
    return 'Try: ' + parts.join(', ');
  }

  // Color-idea thumbnails for the current config: curated mixes first, then one color used
  // uniformly across every color group the config has (wing/backpad/shoulder, plus LX2 pockets
  // when that add-on's colour group exists). Any idea referencing a colour not valid in its group
  // is skipped rather than included half-broken. Returns [] for a config with no wing_color group.
  function ideas(config) {
    var groupsById = {};
    colorGroups(config).forEach(function (cg) { groupsById[cg.group.id] = cg.group; });
    if (!groupsById.wing_color) return [];
    var hasBackpad = !!groupsById.backpad, hasShoulder = !!groupsById.shoulder, hasLx2 = !!groupsById.lx2_color;
    var order = ['wing_color', 'backpad', 'shoulder', 'lx2_color'];
    var out = [];

    if (hasBackpad && hasShoulder) {
      IDEA_MIXES.forEach(function (m) {
        var colors = { wing_color: m.wing_color, backpad: m.backpad, shoulder: m.shoulder };
        var ok = true;
        for (var gid in colors) {
          if (groupsById[gid].colors.indexOf(colors[gid]) < 0) { ok = false; break; }
        }
        if (!ok) return;
        out.push({ id: m.id, label: ideaLabel(colors, order), colors: colors });
      });
    }

    groupsById.wing_color.colors.forEach(function (c) {
      var colors = { wing_color: c };
      if (hasBackpad) {
        if (groupsById.backpad.colors.indexOf(c) < 0) return;
        colors.backpad = c;
      }
      if (hasShoulder) {
        if (groupsById.shoulder.colors.indexOf(c) < 0) return;
        colors.shoulder = c;
      }
      if (hasLx2 && groupsById.lx2_color.colors.indexOf(c) >= 0) colors.lx2_color = c;
      out.push({ id: 'solid-' + c, label: ideaLabel(colors, order), colors: colors });
    });

    return out;
  }

  // Applies an idea's colours to a selection via the normal normalize() path, touching only the
  // colour-group keys the idea names (and only ones that are actually valid groups/colours in this
  // config) — wing/harness/plate/bands/add-ons (and any colour group the idea doesn't mention) pass
  // through untouched. normalize() then drops a colour like lx2_color on its own if its owning
  // add-on isn't currently selected, so a one-colour idea's LX2 colour only "sticks" when LX2 is on.
  function applyIdea(config, sel, idea) {
    var groupsById = {};
    colorGroups(config).forEach(function (cg) { groupsById[cg.group.id] = cg.group; });
    var next = copy(sel);
    for (var gid in idea.colors) {
      var g = groupsById[gid];
      if (!g) continue;
      var c = idea.colors[gid];
      if (g.colors.indexOf(c) < 0) continue;
      next[gid] = c;
    }
    return normalize(config, next);
  }

  // Static sanity checks on a config. Returns an array of error strings ([] = valid).
  function validate(config) {
    var errors = [];
    var choiceSteps = stepsOfType(config, 'choice');
    var addonsSteps = stepsOfType(config, 'addons');
    var allColorGroups = colorGroups(config);

    var sizeValues = [];
    choiceSteps.forEach(function (s) {
      s.options.forEach(function (o) { if (o.sizes) sizeValues = sizeValues.concat(o.sizes); });
    });

    // Every valid selection key, other than 'size' (handled separately below).
    var validKeys = {};
    choiceSteps.forEach(function (s) { validKeys[s.id] = { options: s.options }; });
    allColorGroups.forEach(function (cg) { validKeys[cg.group.id] = { colors: cg.group.colors }; });
    addonsSteps.forEach(function (s) {
      s.options.forEach(function (o) { validKeys['addon:' + o.id] = { addon: true }; });
    });

    // Flag any duplicate SKU in the upgrades array. We don't try to prove two `when` rules can
    // both match at once — a duplicate SKU is never intentional, so it's simplest to always flag it.
    var skuSeen = {};
    config.upgrades.forEach(function (u) {
      if (Object.prototype.hasOwnProperty.call(skuSeen, u.sku)) errors.push('duplicate upgrade sku "' + u.sku + '"');
      skuSeen[u.sku] = true;
    });

    config.upgrades.forEach(function (u) {
      for (var k in u.when) {
        var values = [].concat(u.when[k]);
        if (k === 'size') {
          values.forEach(function (v) {
            if (sizeValues.indexOf(v) < 0) errors.push('upgrade ' + u.sku + ': "size" value "' + v + '" is not a valid size');
          });
          continue;
        }
        if (!Object.prototype.hasOwnProperty.call(validKeys, k)) {
          errors.push('upgrade ' + u.sku + ': unknown when key "' + k + '"');
          continue;
        }
        var vk = validKeys[k];
        values.forEach(function (v) {
          if (vk.addon) { if (v !== true) errors.push('upgrade ' + u.sku + ': addon key "' + k + '" must be true, got ' + JSON.stringify(v)); }
          else if (vk.options) { if (!find(vk.options, v)) errors.push('upgrade ' + u.sku + ': "' + k + '" value "' + v + '" is not a valid option'); }
          else if (vk.colors) { if (vk.colors.indexOf(v) < 0) errors.push('upgrade ' + u.sku + ': "' + k + '" value "' + v + '" is not a valid color'); }
        });
      }
    });

    choiceSteps.forEach(function (s) {
      if (!Object.prototype.hasOwnProperty.call(config.defaults, s.id)) errors.push('missing default for choice step "' + s.id + '"');
    });
    allColorGroups.forEach(function (cg) {
      if (cg.owner) return;
      if (!Object.prototype.hasOwnProperty.call(config.defaults, cg.group.id)) errors.push('missing default for color group "' + cg.group.id + '"');
    });

    var def = defaultSelection(config);
    config.upgrades.forEach(function (u) {
      if (matches(u.when, def)) errors.push('upgrade ' + u.sku + ' matches the default selection');
    });

    // Every choice step's default must be one of its own option ids.
    choiceSteps.forEach(function (s) {
      var d = config.defaults[s.id];
      if (d !== undefined && !find(s.options, d)) errors.push('choice step "' + s.id + '" default "' + d + '" is not a valid option id');
    });

    // Every colour in any colour group must be a known COLORS key.
    allColorGroups.forEach(function (cg) {
      cg.group.colors.forEach(function (c) {
        if (!Object.prototype.hasOwnProperty.call(COLORS, c)) errors.push('color group "' + cg.group.id + '": "' + c + '" is not a key of COLORS');
      });
    });

    // Every colour group's default must be in that group's own colors (checked once per group id).
    var checkedColorDefaults = {};
    allColorGroups.forEach(function (cg) {
      if (Object.prototype.hasOwnProperty.call(checkedColorDefaults, cg.group.id)) return;
      checkedColorDefaults[cg.group.id] = true;
      var d = config.defaults[cg.group.id];
      if (d !== undefined && cg.group.colors.indexOf(d) < 0) errors.push('color group "' + cg.group.id + '" default "' + d + '" is not in its own colors');
    });

    if (config.render) {
      // Any choice-step id or colour-group id (including add-on colour groups, e.g. LX2's
      // lx2_color) is a valid layer colour key.
      var validRenderColorKeys = {};
      allColorGroups.forEach(function (cg) { validRenderColorKeys[cg.group.id] = true; });
      choiceSteps.forEach(function (s) { validRenderColorKeys[s.id] = true; });
      (config.render.layers || []).forEach(function (l) {
        var validKey = !!validRenderColorKeys[l.color];
        if (!validKey && l.default === undefined) {
          errors.push('render layer "' + l.name + '": color key "' + l.color + '" is not a valid choice-step id or color-group id, and has no default');
        } else if (l.default !== undefined && !Object.prototype.hasOwnProperty.call(COLORS, l.default)) {
          errors.push('render layer "' + l.name + '": default "' + l.default + '" is not a key of COLORS');
        }
      });
      (config.render.key || []).forEach(function (k) {
        if (!find(choiceSteps, k)) errors.push('render.key entry "' + k + '" is not a choice step id');
      });
      if (config.render.angles !== undefined) {
        if (!Array.isArray(config.render.angles)) errors.push('render.angles must be an array');
        else config.render.angles.forEach(function (a, i) {
          if (!a || !a.id || !a.label) errors.push('render.angles[' + i + '] needs id and label');
        });
      }
    }

    if (config.size_chart) {
      var sc = config.size_chart, scForOk = false;
      choiceSteps.forEach(function (s) {
        s.options.forEach(function (o) { if (o.id === sc.for && o.sizes) scForOk = true; });
      });
      if (!scForOk) errors.push('size_chart.for "' + sc.for + '" is not a choice option id that has sizes');
    }

    var labels = {};
    function addLabel(label) {
      if (Object.prototype.hasOwnProperty.call(labels, label)) errors.push('summary label collision: "' + label + '"');
      labels[label] = true;
    }
    config.steps.forEach(function (s) {
      if (s.type === 'choice') addLabel(s.label);
      else if (s.type === 'colors') s.groups.forEach(function (g) { addLabel(g.label + ' color'); });
      else if (s.type === 'addons') addLabel(s.label);
    });

    return errors;
  }

  var api = {
    COLORS: COLORS, colorLabel: colorLabel, colorGroups: colorGroups, normalize: normalize,
    defaultSelection: defaultSelection, evaluate: evaluate, optionDeltas: optionDeltas, summary: summary,
    cartItems: cartItems, encodeBuild: encodeBuild, decodeBuild: decodeBuild, renderLayers: renderLayers,
    newBuildId: newBuildId, validate: validate, ideas: ideas, applyIdea: applyIdea, previewAlt: previewAlt
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.CribCore = api;
})(typeof window !== 'undefined' ? window : this);
